import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, Check, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CoperturaTurno } from "../components/PresaVisioneBanner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const TURNO_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte" };
const ymd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function PreseVisione() {
  const { selectedStructure, structures } = useAuth();
  const [data, setData] = useState(ymd());
  const [tipo, setTipo] = useState("dashboard");

  const params = { data, tipo, ...(selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {}) };
  const { data: det } = useQuery({
    queryKey: ["pv-storico", data, tipo, selectedStructure],
    queryFn: async () => (await api.get("/dashboard/presa-visione/dettaglio", { params })).data,
  });
  const turni = det?.turni || [];
  const totConf = turni.reduce((a, t) => a + t.confirmati.length, 0);
  const totAtt = turni.reduce((a, t) => a + t.attesi.length, 0);
  const structName = structures?.find((s) => s.id === selectedStructure)?.nome;

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><ClipboardCheck size={22} /> Storico prese visione</h1>
          <p className="text-sm text-muted-foreground">Chi ha confermato la lettura della dashboard{structName ? ` · ${structName}` : " · tutte le strutture"}</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs font-semibold mb-1 block">Tipo</Label>
            <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5">
              {[["dashboard", "Dashboard"], ["consegne", "Consegne"]].map(([k, lb]) => (
                <button key={k} onClick={() => setTipo(k)} data-testid={`pv-tipo-${k}`}
                  className={`text-sm font-semibold px-3 py-1.5 rounded-md transition-colors ${tipo === k ? "bg-white shadow text-foreground" : "text-muted-foreground"}`}>{lb}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Data</Label>
            <Input type="date" value={data} max={ymd()} onChange={(e) => setData(e.target.value)} className="w-44" data-testid="pv-data" />
          </div>
        </div>
      </div>

      <div className="gr-card p-3 mb-5 flex items-center gap-4 flex-wrap" data-testid="pv-totale">
        <span className="text-sm text-muted-foreground">Copertura del {new Date(data + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</span>
        <div className="flex-1 min-w-[180px]"><CoperturaTurno confirmati={totConf} attesi={totAtt} testid="pv-copertura-giornata" /></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {turni.map((tb) => (
          <div key={tb.turno} className="gr-card p-5" data-testid={`pv-turno-${tb.turno}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-head font-bold">{TURNO_LABEL[tb.turno]}</h2>
            </div>
            <div className="mb-3"><CoperturaTurno confirmati={tb.confirmati.length} attesi={tb.attesi.length} testid={`pv-copertura-${tb.turno}`} /></div>
            {tb.confirmati.length === 0
              ? <p className="text-xs text-muted-foreground">Nessuna conferma</p>
              : <div className="space-y-1.5">
                  {tb.confirmati.map((c, i) => (
                    <div key={i} data-testid={`pv-conf-${tb.turno}-${i}`} className="text-sm flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5">
                      <span className="font-semibold truncate inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> {c.operatore_nome}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{hhmm(c.ts)}</span>
                    </div>
                  ))}
                </div>}
            {tb.mancanti.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 flex items-start gap-1.5" data-testid={`pv-mancanti-${tb.turno}`}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>Non ha confermato: {tb.mancanti.join(", ")}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
